import React from 'react';
import PluginRenderingBar from './PluginRenderingBar';
import PluginSlotBadge from './PluginSlotBadge';
import './ContentPlugins.css';

function isVideoUrl(url) {
  if (!url) return false;
  return /\.(mp4|webm|mov)(\?|$)/i.test(url);
}

function formatReviewDate(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString();
  } catch {
    return null;
  }
}

const PENDING_MESSAGE_NEW =
  'Your content has been submitted for review. It is not visible on your screen until an admin approves it.';
const PENDING_MESSAGE_UPDATE =
  'Your update is pending review. Your current content remains on screen until it is approved.';

export default function ContentReviewStatusModal({
  open,
  mode,
  slot,
  displaySlotNumber,
  onClose,
  onEdit,
  onClear,
}) {
  if (!open || !slot) return null;

  const previewUrl = mode === 'pending'
    ? slot.pendingContentUrl
    : (slot.contentUrl || slot.pendingContentUrl);
  const hasLive = Boolean(slot.contentUrl);
  const isPlugin = mode === 'pending'
    ? slot.pendingContentType === 'plugin'
    : slot.contentType === 'plugin';
  const pluginId = mode === 'pending' ? slot.pendingPluginId : slot.pluginId;

  let title = 'Content under review';
  let message = hasLive ? PENDING_MESSAGE_UPDATE : PENDING_MESSAGE_NEW;
  let showProgress = mode === 'pending';

  if (mode === 'approved') {
    title = 'Content approved';
    message = 'Your content was approved and is now live on your screen.';
    showProgress = false;
  } else if (mode === 'rejected') {
    title = 'Content not approved';
    message = hasLive
      ? 'Your submission was not approved. Your current live content is unchanged.'
      : 'Your submission was not approved and was not added to your screen.';
    showProgress = false;
  }

  const reviewDate = formatReviewDate(
    mode === 'pending' ? slot.pendingSubmittedAt : slot.lastReviewAt,
  );

  return (
    <div className="contentPluginOverlay" role="presentation" onClick={onClose}>
      <div
        className="contentPluginConfigModal contentReviewStatusModal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="contentPluginConfigHeader">
          <h2>{title}</h2>
          <button type="button" className="contentPluginClose" onClick={onClose}>×</button>
        </div>

        {showProgress ? (
          <PluginRenderingBar message="Waiting for admin approval… You can close this window and check back later." />
        ) : null}

        <div className="contentPluginConfigBody contentReviewStatusBody">
          <p className="contentReviewStatusMessage">{message}</p>
          {reviewDate ? (
            <p className="contentReviewStatusMeta">
              {mode === 'pending' ? 'Submitted' : 'Reviewed'} {reviewDate}
            </p>
          ) : null}

          {slot.lastReviewNotes && (mode === 'approved' || mode === 'rejected') ? (
            <div className="contentReviewNotesBlock">
              <span className="contentReviewNotesLabel">Note from review</span>
              <p className="contentReviewNotesText">{slot.lastReviewNotes}</p>
            </div>
          ) : null}

          {previewUrl ? (
            <div className="contentReviewPreviewWrap">
              {isPlugin && pluginId ? <PluginSlotBadge pluginId={pluginId} /> : null}
              {isVideoUrl(previewUrl) ? (
                <video
                  src={previewUrl}
                  className="contentReviewPreviewMedia"
                  controls
                  muted
                  playsInline
                  loop
                />
              ) : (
                <img src={previewUrl} alt={`Slot ${displaySlotNumber} preview`} className="contentReviewPreviewMedia" />
              )}
            </div>
          ) : null}

          <p className="contentReviewSlotLabel">Personal content slot {displaySlotNumber}</p>
        </div>

        <div className="contentPluginConfigFooter contentReviewStatusFooter">
          {mode === 'pending' && onEdit ? (
            <button type="button" className="contentPluginCancel" onClick={onEdit}>
              Replace submission
            </button>
          ) : null}
          {mode === 'pending' && onClear ? (
            <button type="button" className="contentPluginCancel" onClick={onClear}>
              Cancel submission
            </button>
          ) : null}
          <button type="button" className="contentPluginSave" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
